import React, {useCallback, useEffect, useState} from 'react';
import {detect} from 'detect-browser';
import type {AstView} from 'rendition-core';

import Button from 'Components/Tailwind/Button';
import {PublishIcon} from 'Views/Preview/Publish/Publish';
import {PublishDialog} from 'Views/Preview/Publish/PublishDialog';
import type {WebflowControls} from 'Views/Preview/useWebflowJSON';
import {CodeIcon} from 'Icons/CodeIcon';
import {useProductContext} from 'Contexts/ProductContext';
import {AIGenLink} from 'Components/AIGenLink';
interface Props {
    loadingCode: boolean;
    webflowControls: WebflowControls;
    onGetCode: () => void;
    view: AstView;
    id: string;
    selectedId: string;
}
const baseClasses =
    'px-4 py-2 basis-10 shrink-0 font-semibold text-[13px] text-white bg-transparent border rounded flex justify-center items-center';
const blueButtonStyle = baseClasses + ' border-sky-500 hover:border-transparent hover:bg-sky-500';

const supportedBrowsersToolTipText = `
    Supported Browsers:
    Chrome, Firefox, Edge

    Copying and Pasting not supported in Safari.
`;

export const DocumentControls: React.FC<Props> = ({id, loadingCode, view, onGetCode, webflowControls, selectedId}) => {
    const [supportedBrowser, setSupportedBrowser] = useState(true);
    const [showSuccessMsg, setShowSuccessMsg] = useState(false);

    useEffect(() => {
        const browser = detect();

        if (browser && browser.name === 'safari') {
            setSupportedBrowser(false);
        }
    }, []);

    const executeCopyForWebFlow = useCallback(() => {
        webflowControls.copyWebflowJson();
        setShowSuccessMsg(true);
    }, [webflowControls]);

    useEffect(() => {
        if (showSuccessMsg) {
            const timeout = setTimeout(() => {
                setShowSuccessMsg(false);
            }, 5000);
            return () => {
                timeout && clearTimeout(timeout);
            };
        }
    }, [showSuccessMsg]);

    const publishSite = useCallback(() => {
        // Open a new tab:
        window.open(`${window.location.origin}/publish/${id}`);
    }, [id]);

    const {mode} = useProductContext();

    if (mode === 'Rendition_Code') {
        return null;
    }

    return (
        <div className="flex flex-col gap-3 py-2">
            {id && <AIGenLink buttonClassNames="bg-neutral-900/90" id={id} />}
            <Button id="rendition-sandbox-btn" onClick={onGetCode} isLoading={loadingCode} outlined>
                <CodeIcon className="mr-2" /> Code Sandbox
            </Button>
            {view.meta.assetUrlZip && (
                <a
                    style={{appearance: 'button'}}
                    className={blueButtonStyle}
                    download="zipFolder"
                    id="rendition-download-assets-btn"
                    href={view.meta.assetUrlZip}
                >
                    Download Assets
                </a>
            )}
            <Button
                title={supportedBrowser ? '' : supportedBrowsersToolTipText}
                disabled={
                    !supportedBrowser ||
                    webflowControls.webflowLoading ||
                    webflowControls.webflowError ||
                    !webflowControls.webflowJson
                }
                onClick={executeCopyForWebFlow}
                isLoading={webflowControls.webflowLoading}
                outlined
                error={webflowControls.webflowError}
                id="rendition-copy-webflow-btn"
            >
                {showSuccessMsg && !webflowControls.webflowError ? 'Copied' : 'Copy For Webflow'}
            </Button>
            <div className="flex space-x-3">
                <Button onClick={publishSite} outlined>
                    <PublishIcon />
                </Button>
                <PublishDialog pageId={id} projectId={view.meta.projectId!} />
            </div>
        </div>
    );
};
